import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Bot,
  Zap,
  Cpu,
  Clock,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Search,
  Filter,
  RotateCcw,
  Eye,
  X,
  MessageSquare,
  Building2,
  ChevronLeft,
  ChevronRight,
  Copy,
  Check,
  GitBranch,
  RefreshCw,
  Calendar,
  DollarSign,
  Calculator,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { TENANT_ID, API_BASE_URL } from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { formatCostUSD, formatTokenCount } from '../utils/aiPricing';

export interface AIMessageUsageRecord {
  id: string;
  tenantId: string;
  tenantName: string | null;
  tenantSlug: string | null;
  conversationId: string | null;
  messageId: string | null;
  provider: string;
  model: string;
  type: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  latencyMs: number;
  customerMessage: string | null;
  botResponse: string | null;
  node: string | null;
  status: string;
  errorText: string | null;
  createdAt: string;
  estimatedCostUSD?: number;
  promptCostUSD?: number;
  completionCostUSD?: number;
  isFreeTier?: boolean;
}

export interface AIMessageUsageSummary {
  totalTokens: number;
  promptTokens: number;
  completionTokens: number;
  avgLatencyMs: number;
  totalRequests: number;
  totalCostUSD?: number;
  avgCostPerRequestUSD?: number;
  avgTokensPerRequest?: number;
  paidRequests?: number;
  freeTierRequests?: number;
}

export interface AIMessageUsageResponse {
  records: AIMessageUsageRecord[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  summary: AIMessageUsageSummary;
}

export interface TenantOption {
  tenantId: string;
  tenantName: string | null;
  tenantSlug: string | null;
}

interface AIMessageUsageTableProps {
  tenants?: TenantOption[];
  initialTenantId?: string;
  initialProvider?: string;
}

const PROVIDER_BADGE_CLASSES: Record<string, string> = {
  groq: 'provider-pill-groq',
  openrouter: 'provider-pill-openrouter',
  mistral: 'provider-pill-mistral',
  gemini: 'provider-pill-gemini',
  nvidia: 'provider-pill-nvidia',
  openai: 'provider-pill-openai',
  ollama: 'provider-pill-ollama',
  cerebras: 'provider-pill-cerebras',
  jev: 'provider-pill-jev',
  opencode: 'provider-pill-opencode',
  deepseek: 'provider-pill-deepseek',
};

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(value);
    }, delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

export const AIMessageUsageTable: React.FC<AIMessageUsageTableProps> = ({
  tenants = [],
  initialTenantId = '',
  initialProvider = '',
}) => {
  const { token } = useAuth();
  const { addToast } = useToast();

  const [records, setRecords] = useState<AIMessageUsageRecord[]>([]);
  const [summary, setSummary] = useState<AIMessageUsageSummary>({
    totalTokens: 0,
    promptTokens: 0,
    completionTokens: 0,
    avgLatencyMs: 0,
    totalRequests: 0,
    totalCostUSD: 0,
    avgCostPerRequestUSD: 0,
    avgTokensPerRequest: 0,
    paidRequests: 0,
    freeTierRequests: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Filters state
  const [tenantFilter, setTenantFilter] = useState(initialTenantId);
  const [providerFilter, setProviderFilter] = useState(initialProvider);
  const [modelFilter, setModelFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');

  // Sync external drill-down filters when provided
  useEffect(() => {
    if (initialTenantId !== undefined) {
      setTenantFilter(initialTenantId);
      setPage(1);
    }
  }, [initialTenantId]);

  useEffect(() => {
    if (initialProvider !== undefined) {
      setProviderFilter(initialProvider);
      setPage(1);
    }
  }, [initialProvider]);

  const debouncedSearch = useDebouncedValue(searchFilter, 350);
  const debouncedModel = useDebouncedValue(modelFilter, 350);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });

  // Detail Modal & Copy state
  const [selectedRecord, setSelectedRecord] = useState<AIMessageUsageRecord | null>(null);
  const [copiedField, setCopiedField] = useState<'input' | 'output' | null>(null);

  useEffect(() => {
    if (!selectedRecord) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedRecord(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedRecord]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (tenantFilter.trim()) params.append('tenantId', tenantFilter.trim());
      if (providerFilter.trim()) params.append('provider', providerFilter.trim());
      if (debouncedModel.trim()) params.append('model', debouncedModel.trim());
      if (typeFilter.trim()) params.append('type', typeFilter.trim());
      if (statusFilter.trim()) params.append('status', statusFilter.trim());
      if (debouncedSearch.trim()) params.append('search', debouncedSearch.trim());
      if (startDateFilter) params.append('startDate', startDateFilter);
      if (endDateFilter) params.append('endDate', endDateFilter);
      params.append('page', page.toString());
      params.append('limit', limit.toString());

      const res = await fetch(`${API_BASE_URL}/admin/ai-usage/messages?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
        },
      });

      if (!res.ok) {
        throw new Error(`Error al consultar telemetría (${res.status})`);
      }

      const data: AIMessageUsageResponse = await res.json();
      setRecords(data.records);
      setPagination(data.pagination);
      setSummary(data.summary);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error inesperado';
      setError(msg);
      addToast({ type: 'error', title: 'Error', message: msg });
    } finally {
      setLoading(false);
    }
  }, [
    token,
    tenantFilter,
    providerFilter,
    debouncedModel,
    typeFilter,
    statusFilter,
    debouncedSearch,
    startDateFilter,
    endDateFilter,
    page,
    limit,
    addToast,
  ]);

  useEffect(() => {
    void fetchRecords();
  }, [fetchRecords]);

  const handleResetFilters = () => {
    setTenantFilter('');
    setProviderFilter('');
    setModelFilter('');
    setTypeFilter('');
    setStatusFilter('');
    setSearchFilter('');
    setStartDateFilter('');
    setEndDateFilter('');
    setPage(1);
  };

  const applyDatePreset = (days: number | null) => {
    if (days === null) {
      setStartDateFilter('');
      setEndDateFilter('');
      setPage(1);
      return;
    }
    const end = new Date();
    const start = new Date();
    if (days > 0) {
      start.setDate(end.getDate() - days);
    }
    const formatIsoDate = (d: Date) => d.toISOString().split('T')[0] || '';
    setStartDateFilter(formatIsoDate(start));
    setEndDateFilter(formatIsoDate(end));
    setPage(1);
  };

  const handleCopyText = async (text: string | null, field: 'input' | 'output') => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      addToast({ type: 'error', title: 'Error', message: 'No se pudo copiar al portapapeles' });
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleString('es-ES', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const getProviderClass = (provider: string) => {
    const key = provider.toLowerCase();
    return PROVIDER_BADGE_CLASSES[key] || 'provider-pill-default';
  };

  const getLatencyClass = (ms: number) => {
    if (ms <= 0) return 'latency-neutral';
    if (ms < 1500) return 'latency-fast';
    if (ms < 4000) return 'latency-medium';
    return 'latency-slow';
  };

  const hasActiveFilters = Boolean(
    tenantFilter ||
      providerFilter ||
      modelFilter ||
      typeFilter ||
      statusFilter ||
      searchFilter ||
      startDateFilter ||
      endDateFilter
  );

  const avgTokensPerRequest =
    summary.avgTokensPerRequest && summary.avgTokensPerRequest > 0
      ? summary.avgTokensPerRequest
      : summary.totalRequests > 0
      ? Math.round(summary.totalTokens / summary.totalRequests)
      : 0;
  const avgPromptPerRequest =
    summary.totalRequests > 0 ? Math.round(summary.promptTokens / summary.totalRequests) : 0;
  const avgCompletionPerRequest =
    summary.totalRequests > 0 ? Math.round(summary.completionTokens / summary.totalRequests) : 0;
  const avgCostPerRequest =
    summary.avgCostPerRequestUSD ??
    (summary.totalRequests > 0 ? (summary.totalCostUSD ?? 0) / summary.totalRequests : 0);

  return (
    <div className="ai-messages-telemetry">
      {/* KPI Summary Cards */}
      <div className="telemetry-kpi-grid">
        <div className="telemetry-kpi-card kpi-blue">
          <div className="telemetry-kpi-header">
            <Bot size={18} className="kpi-icon-blue" />
            <span>Llamadas LLM</span>
          </div>
          <div className="telemetry-kpi-value">{summary.totalRequests.toLocaleString()}</div>
          <div className="telemetry-kpi-meta">En el rango filtrado</div>
        </div>

        <div className="telemetry-kpi-card kpi-emerald">
          <div className="telemetry-kpi-header">
            <Zap size={18} className="kpi-icon-emerald" />
            <span>Total Tokens</span>
          </div>
          <div className="telemetry-kpi-value">{formatTokenCount(summary.totalTokens)}</div>
          <div className="telemetry-kpi-meta">
            Entrada: {formatTokenCount(summary.promptTokens)} • Salida: {formatTokenCount(summary.completionTokens)}
          </div>
        </div>

        <div className="telemetry-kpi-card kpi-violet">
          <div className="telemetry-kpi-header">
            <DollarSign size={18} className="kpi-icon-violet" />
            <span>Costo Total Est.</span>
          </div>
          <div className="telemetry-kpi-value font-mono">
            {formatCostUSD(summary.totalCostUSD ?? 0)}
          </div>
          <div className="telemetry-kpi-meta">
            Promedio: {formatCostUSD(summary.avgCostPerRequestUSD ?? 0)}/req • {summary.freeTierRequests ?? 0} free
          </div>
        </div>

        <div className="telemetry-kpi-card kpi-indigo">
          <div className="telemetry-kpi-header">
            <Cpu size={18} className="kpi-icon-indigo" />
            <span>Tokens / Mensaje</span>
          </div>
          <div className="telemetry-kpi-value font-mono">
            ~{avgTokensPerRequest.toLocaleString()} tok
          </div>
          <div className="telemetry-kpi-meta">
            ~{avgPromptPerRequest} in / {avgCompletionPerRequest} out • {formatCostUSD(avgCostPerRequest)}/msg
          </div>
        </div>

        <div className="telemetry-kpi-card kpi-amber">
          <div className="telemetry-kpi-header">
            <Clock size={18} className="kpi-icon-amber" />
            <span>Latencia Promedio</span>
          </div>
          <div className="telemetry-kpi-value">{summary.avgLatencyMs} ms</div>
          <div className="telemetry-kpi-meta">Tiempo de respuesta del modelo</div>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="telemetry-filter-panel">
        <div className="telemetry-filter-header">
          <div className="telemetry-filter-title">
            <Filter size={16} />
            <span>Filtros de Búsqueda</span>
            {loading && records.length > 0 && (
              <span className="telemetry-fetching-badge">
                <RefreshCw size={12} className="spin-icon" />
                Actualizando...
              </span>
            )}
          </div>

          <div className="telemetry-date-presets">
            <Calendar size={14} className="preset-calendar-icon" />
            <button type="button" className="preset-chip" onClick={() => applyDatePreset(0)}>
              Hoy
            </button>
            <button type="button" className="preset-chip" onClick={() => applyDatePreset(7)}>
              7 días
            </button>
            <button type="button" className="preset-chip" onClick={() => applyDatePreset(30)}>
              30 días
            </button>
            {hasActiveFilters && (
              <button type="button" className="btn-reset-inline" onClick={handleResetFilters}>
                <RotateCcw size={13} />
                <span>Limpiar Filtros</span>
              </button>
            )}
          </div>
        </div>

        <div className="telemetry-filter-grid">
          {/* Search Text */}
          <div className="filter-item">
            <label htmlFor="filter-search">Buscar en Mensajes</label>
            <div className="input-with-icon">
              <Search size={14} className="input-leading-icon" />
              <input
                id="filter-search"
                type="text"
                placeholder="Texto cliente o bot..."
                value={searchFilter}
                onChange={(e) => {
                  setSearchFilter(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>

          {/* Provider */}
          <div className="filter-item">
            <label htmlFor="filter-provider">Proveedor Router</label>
            <select
              id="filter-provider"
              value={providerFilter}
              onChange={(e) => {
                setProviderFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todos los Proveedores</option>
              <option value="groq">Groq</option>
              <option value="openrouter">OpenRouter</option>
              <option value="mistral">Mistral</option>
              <option value="gemini">Gemini</option>
              <option value="nvidia">Nvidia</option>
              <option value="ollama">Ollama</option>
              <option value="openai">OpenAI</option>
              <option value="cerebras">Cerebras</option>
              <option value="jev">Jev (System One)</option>
              <option value="opencode">OpenCode Zen</option>
              <option value="deepseek">DeepSeek</option>
            </select>
          </div>

          {/* Model */}
          <div className="filter-item">
            <label htmlFor="filter-model">Modelo LLM</label>
            <input
              id="filter-model"
              type="text"
              placeholder="Ej: llama-3.3, gpt-4o..."
              value={modelFilter}
              onChange={(e) => {
                setModelFilter(e.target.value);
                setPage(1);
              }}
            />
          </div>

          {/* Type */}
          <div className="filter-item">
            <label htmlFor="filter-type">Tipo de Ejecución</label>
            <select
              id="filter-type"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todos los Tipos</option>
              <option value="chat">Chat (Conversación)</option>
              <option value="structured">Estructurado (JSON)</option>
              <option value="tool">Herramienta (Tool Call)</option>
              <option value="system_one">System One (Jev)</option>
            </select>
          </div>

          {/* Status */}
          <div className="filter-item">
            <label htmlFor="filter-status">Estado</label>
            <select
              id="filter-status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todos los Estados</option>
              <option value="success">Éxito</option>
              <option value="fallback">Fallback</option>
              <option value="error">Error</option>
            </select>
          </div>

          {/* Tenant */}
          <div className="filter-item">
            <label htmlFor="filter-tenant">Tenant</label>
            {tenants.length > 0 ? (
              <select
                id="filter-tenant"
                value={tenantFilter}
                onChange={(e) => {
                  setTenantFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">Todos los Tenants</option>
                {tenants.map((t) => (
                  <option key={t.tenantId} value={t.tenantId}>
                    {t.tenantName || t.tenantSlug || t.tenantId.substring(0, 10)}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id="filter-tenant"
                type="text"
                placeholder="ID del Tenant..."
                value={tenantFilter}
                onChange={(e) => {
                  setTenantFilter(e.target.value);
                  setPage(1);
                }}
              />
            )}
          </div>

          {/* Start Date */}
          <div className="filter-item">
            <label htmlFor="filter-start-date">Desde</label>
            <input
              id="filter-start-date"
              type="date"
              value={startDateFilter}
              onChange={(e) => {
                setStartDateFilter(e.target.value);
                setPage(1);
              }}
            />
          </div>

          {/* End Date */}
          <div className="filter-item">
            <label htmlFor="filter-end-date">Hasta</label>
            <input
              id="filter-end-date"
              type="date"
              value={endDateFilter}
              onChange={(e) => {
                setEndDateFilter(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      {/* State Views */}
      {loading && records.length === 0 ? (
        <div className="records-table-card" aria-busy="true" aria-label="Cargando telemetría de mensajes LLM">
          <div className="skeleton-table-wrapper">
            {Array.from({ length: 6 }).map((_, idx) => (
              <div key={`skeleton-row-${idx}`} className="skeleton-table-row">
                <div className="skeleton-cell w-24" />
                <div className="skeleton-cell w-32" />
                <div className="skeleton-cell w-48" />
                <div className="skeleton-cell w-48" />
                <div className="skeleton-cell w-28" />
                <div className="skeleton-cell w-20" />
                <div className="skeleton-cell w-16" />
              </div>
            ))}
          </div>
        </div>
      ) : error ? (
        <div className="telemetry-error-state" role="alert">
          <AlertCircle size={36} className="error-state-icon" />
          <h3>No se pudo cargar la telemetría</h3>
          <p>{error}</p>
          <button type="button" className="btn btn-secondary" onClick={fetchRecords}>
            Reintentar
          </button>
        </div>
      ) : records.length === 0 ? (
        <div className="telemetry-empty-state">
          <Bot size={40} className="empty-state-bot-icon" />
          <h3>No hay registros de IA por mensaje</h3>
          <p>No se encontraron llamadas del AI Router para los filtros especificados.</p>
          <button type="button" className="btn btn-secondary" onClick={handleResetFilters}>
            Restablecer Filtros
          </button>
        </div>
      ) : (
        <>
          {/* Table Container */}
          <div className="records-table-card">
            <div className="records-table-scroll">
              <table className="modern-table telemetry-table">
                <thead>
                  <tr>
                    <th>Fecha / Hora</th>
                    <th>Tenant</th>
                    <th>Mensaje Cliente</th>
                    <th>Respuesta IA</th>
                    <th>Proveedor y Modelo</th>
                    <th>Tipo</th>
                    <th className="text-right">Tokens</th>
                    <th className="text-right">Costo Est. ($)</th>
                    <th className="text-right">Latencia</th>
                    <th className="text-center">Estado</th>
                    <th className="text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r) => (
                    <tr key={r.id} onClick={() => setSelectedRecord(r)} className="clickable-row">
                      {/* Fecha / Hora */}
                      <td className="cell-timestamp">
                        <div>{formatDate(r.createdAt)}</div>
                        {r.node && <span className="cell-node-tag">{r.node}</span>}
                      </td>

                      {/* Tenant */}
                      <td className="cell-tenant">
                        <div className="tenant-badge-inline" title={r.tenantId}>
                          <Building2 size={14} className="tenant-inline-icon" />
                          <span>{r.tenantName || r.tenantSlug || `${r.tenantId.substring(0, 8)}...`}</span>
                        </div>
                      </td>

                      {/* Mensaje Cliente */}
                      <td className="cell-message-preview">
                        <div className="line-clamp-2" title={r.customerMessage || 'Sin texto de entrada'}>
                          {r.customerMessage || <span className="text-muted-italic">Sin entrada</span>}
                        </div>
                      </td>

                      {/* Respuesta Bot */}
                      <td className="cell-response-preview">
                        <div className="line-clamp-2" title={r.botResponse || ''}>
                          {r.botResponse || <span className="text-muted-italic">Sin salida</span>}
                        </div>
                      </td>

                      {/* Proveedor + Modelo LLM */}
                      <td className="cell-provider-model">
                        <div className="provider-model-stack">
                          <span className={`provider-pill ${getProviderClass(r.provider)}`}>
                            <Zap size={11} />
                            {r.provider}
                          </span>
                          <code className="model-code-badge" title={r.model}>
                            {r.model}
                          </code>
                        </div>
                      </td>

                      {/* Tipo */}
                      <td>
                        <span className={`exec-type-pill type-${r.type}`}>
                          {r.type}
                        </span>
                      </td>

                      {/* Tokens */}
                      <td className="text-right cell-tokens">
                        <div className="tokens-total">{r.totalTokens.toLocaleString()}</div>
                        <div className="tokens-split">
                          {r.promptTokens} in / {r.completionTokens} out
                        </div>
                      </td>

                      {/* Costo Estimado ($ USD) */}
                      <td className="text-right cell-cost">
                        {r.isFreeTier ? (
                          <span className="free-tier-pill" title="Modelo Free Tier ($0.00 / token)">
                            Free Tier
                          </span>
                        ) : (
                          <span
                            className="cost-pill font-mono"
                            title={`In: ${formatCostUSD(r.promptCostUSD || 0)} | Out: ${formatCostUSD(r.completionCostUSD || 0)}`}
                          >
                            {formatCostUSD(r.estimatedCostUSD ?? 0)}
                          </span>
                        )}
                      </td>

                      {/* Latencia */}
                      <td className="text-right cell-latency">
                        {r.latencyMs > 0 ? (
                          <span className={`latency-pill ${getLatencyClass(r.latencyMs)}`}>
                            {r.latencyMs} ms
                          </span>
                        ) : (
                          <span className="text-muted-italic">-</span>
                        )}
                      </td>

                      {/* Estado */}
                      <td className="text-center">
                        {r.status === 'success' ? (
                          <span className="status-pill status-success" title="Respuesta exitosa">
                            <CheckCircle2 size={13} />
                            <span>Éxito</span>
                          </span>
                        ) : r.status === 'fallback' ? (
                          <span className="status-pill status-fallback" title="Degradado a fallback">
                            <AlertTriangle size={13} />
                            <span>Fallback</span>
                          </span>
                        ) : (
                          <span className="status-pill status-error" title={r.errorText || 'Error en llamada'}>
                            <AlertCircle size={13} />
                            <span>Error</span>
                          </span>
                        )}
                      </td>

                      {/* Botón Detalle */}
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="btn-inspect"
                          onClick={() => setSelectedRecord(r)}
                          aria-label={`Ver detalles de la llamada ${r.id}`}
                        >
                          <Eye size={13} />
                          <span>Detalle</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Bar */}
            <div className="telemetry-pagination-bar">
              <div className="pagination-meta">
                <label htmlFor="telemetry-page-limit">Mostrar:</label>
                <select
                  id="telemetry-page-limit"
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>• Total: {pagination.total.toLocaleString()} llamadas</span>
              </div>

              <div className="pagination-controls">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft size={15} />
                  <span>Anterior</span>
                </button>

                <span className="pagination-page-indicator">
                  Página {page} de {Math.max(1, pagination.totalPages)}
                </span>

                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={page >= pagination.totalPages}
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                >
                  <span>Siguiente</span>
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Detail Inspection Modal */}
      {selectedRecord && (
        <div
          className="telemetry-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          onClick={() => setSelectedRecord(null)}
        >
          <div className="telemetry-modal-dialog" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="telemetry-modal-header">
              <div>
                <h3 id="modal-title" className="telemetry-modal-title">
                  <Cpu size={20} className="kpi-icon-blue" />
                  <span>Inspección de Telemetría LLM</span>
                </h3>
                <p className="telemetry-modal-subtitle">
                  ID: {selectedRecord.id} • {formatDate(selectedRecord.createdAt)}
                </p>
              </div>
              <div className="telemetry-modal-header-actions">
                {selectedRecord.conversationId && (
                  <Link
                    to={`/admin/debug?conversationId=${encodeURIComponent(selectedRecord.conversationId)}`}
                    className="btn-flow-debugger-link"
                  >
                    <GitBranch size={14} />
                    <span>Abrir Flow Debugger</span>
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => setSelectedRecord(null)}
                  aria-label="Cerrar modal"
                  className="btn-modal-close"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Metadata Badges Grid */}
            <div className="telemetry-modal-meta-grid">
              <div className="meta-box">
                <span className="meta-label">Proveedor Router</span>
                <span className="meta-value capitalize">{selectedRecord.provider}</span>
              </div>
              <div className="meta-box">
                <span className="meta-label">Modelo LLM</span>
                <code className="model-code-badge">{selectedRecord.model}</code>
              </div>
              <div className="meta-box">
                <span className="meta-label">Tokens Totales</span>
                <span className="meta-value">
                  {selectedRecord.totalTokens.toLocaleString()}{' '}
                  <small className="meta-sub">
                    ({selectedRecord.promptTokens} in / {selectedRecord.completionTokens} out)
                  </small>
                </span>
              </div>
              <div className="meta-box">
                <span className="meta-label">Costo Estimado</span>
                <span className="meta-value font-mono">
                  {selectedRecord.isFreeTier ? (
                    <span className="free-tier-pill">Free Tier ($0.00)</span>
                  ) : (
                    formatCostUSD(selectedRecord.estimatedCostUSD ?? 0)
                  )}
                  {selectedRecord.promptCostUSD !== undefined && selectedRecord.completionCostUSD !== undefined && !selectedRecord.isFreeTier && (
                    <small className="meta-sub">
                      ({formatCostUSD(selectedRecord.promptCostUSD)} in + {formatCostUSD(selectedRecord.completionCostUSD)} out)
                    </small>
                  )}
                </span>
              </div>
              <div className="meta-box">
                <span className="meta-label">Latencia</span>
                <span className={`latency-pill ${getLatencyClass(selectedRecord.latencyMs)}`}>
                  {selectedRecord.latencyMs} ms
                </span>
              </div>
              <div className="meta-box">
                <span className="meta-label">Tenant</span>
                <span className="meta-value">
                  {selectedRecord.tenantName || selectedRecord.tenantSlug || selectedRecord.tenantId}
                </span>
              </div>
              <div className="meta-box">
                <span className="meta-label">Tipo / Nodo / Estado</span>
                <span className="meta-value">
                  {selectedRecord.type} {selectedRecord.node ? `• ${selectedRecord.node}` : ''} •{' '}
                  <strong className={selectedRecord.status === 'success' ? 'text-success' : 'text-danger'}>
                    {selectedRecord.status.toUpperCase()}
                  </strong>
                </span>
              </div>
            </div>

            {/* Detailed Financial Calculation Card */}
            <div className="telemetry-financial-breakdown">
              <div className="financial-breakdown-header">
                <Calculator size={15} className="kpi-icon-violet" />
                <span className="font-semibold text-sm">Estructura de Costo del Mensaje</span>
              </div>
              <div className="financial-breakdown-content">
                <div className="breakdown-col">
                  <span className="breakdown-label">Tokens Entrada (Prompt)</span>
                  <span className="breakdown-value font-mono">
                    {selectedRecord.promptTokens.toLocaleString()} tokens
                  </span>
                  <span className="breakdown-sub font-mono">
                    {selectedRecord.isFreeTier ? '$0.00 (Free Tier)' : formatCostUSD(selectedRecord.promptCostUSD ?? 0)}
                  </span>
                </div>
                <div className="breakdown-col">
                  <span className="breakdown-label">Tokens Salida (Completion)</span>
                  <span className="breakdown-value font-mono">
                    {selectedRecord.completionTokens.toLocaleString()} tokens
                  </span>
                  <span className="breakdown-sub font-mono">
                    {selectedRecord.isFreeTier ? '$0.00 (Free Tier)' : formatCostUSD(selectedRecord.completionCostUSD ?? 0)}
                  </span>
                </div>
                <div className="breakdown-col total-col">
                  <span className="breakdown-label">Total Facturable</span>
                  <span className="breakdown-value font-mono text-violet font-bold">
                    {selectedRecord.isFreeTier ? '$0.00 USD' : formatCostUSD(selectedRecord.estimatedCostUSD ?? 0)}
                  </span>
                  <span className="breakdown-sub">
                    {selectedRecord.isFreeTier ? 'Modelo de costo cero' : 'Costo estimado de la llamada'}
                  </span>
                </div>
              </div>
            </div>

            {/* Customer Message Box */}
            <div className="telemetry-payload-section">
              <div className="telemetry-payload-header">
                <label className="telemetry-payload-label">
                  <MessageSquare size={15} />
                  <span>Mensaje del Cliente (Input)</span>
                </label>
                {selectedRecord.customerMessage && (
                  <button
                    type="button"
                    className="btn-copy-payload"
                    onClick={() => handleCopyText(selectedRecord.customerMessage, 'input')}
                  >
                    {copiedField === 'input' ? <Check size={13} /> : <Copy size={13} />}
                    <span>{copiedField === 'input' ? 'Copiado' : 'Copiar'}</span>
                  </button>
                )}
              </div>
              <div className="telemetry-payload-box input-box">
                {selectedRecord.customerMessage || (
                  <span className="text-muted-italic">Sin entrada de cliente registrada</span>
                )}
              </div>
            </div>

            {/* AI Output Box */}
            <div className="telemetry-payload-section">
              <div className="telemetry-payload-header">
                <label className="telemetry-payload-label">
                  <Bot size={15} />
                  <span>Respuesta Generada por la IA (Output)</span>
                </label>
                {selectedRecord.botResponse && (
                  <button
                    type="button"
                    className="btn-copy-payload"
                    onClick={() => handleCopyText(selectedRecord.botResponse, 'output')}
                  >
                    {copiedField === 'output' ? <Check size={13} /> : <Copy size={13} />}
                    <span>{copiedField === 'output' ? 'Copiado' : 'Copiar'}</span>
                  </button>
                )}
              </div>
              <div
                className={`telemetry-payload-box output-box ${
                  selectedRecord.type !== 'chat' ? 'mono-font' : ''
                }`}
              >
                {selectedRecord.botResponse || <span className="text-muted-italic">Sin salida registrada</span>}
              </div>
            </div>

            {/* Error detail if any */}
            {selectedRecord.errorText && (
              <div className="telemetry-error-box">
                <span className="error-box-title">Detalle del Error:</span>
                <code>{selectedRecord.errorText}</code>
              </div>
            )}

            {/* Modal Footer */}
            <div className="telemetry-modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setSelectedRecord(null)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIMessageUsageTable;